import { useState } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { useFavouriteHotels } from 'hooks/useFavouriteHotels';
import { isFavourite } from 'utils/favouriteHotels';

// WHIT-485 round 3: the heart that adds a hotel to the favourite hotels
// (/kedvelt-szallasok/) or takes it off.

const FavouriteButton: React.FC<{
  hotelId: string;
  name: string;
  className?: string;
}> = ({ hotelId, name, className = '' }) => {
  const { t } = useTranslation();
  const { hotels, setFavourite } = useFavouriteHotels();
  const [problem, setProblem] = useState<string | null>(null);
  // One change at a time from this heart: a second click waits for the first.
  const [busy, setBusy] = useState(false);
  const on = isFavourite(hotels, hotelId);
  const label = on ? t('Remove from favourites') : t('Add to favourites');

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    const code = await setFavourite({ hotelId, name }, !on);
    setBusy(false);
    if (code === 'TOO_MANY') {
      setProblem(t('Your favourites are full. Remove one to add another.'));
    } else if (code) {
      setProblem(t('Favourites cannot be changed right now.'));
    }
  };

  return (
    <span className={`inline-flex flex-col items-end ${className}`}>
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        aria-busy={busy}
        aria-pressed={on}
        aria-label={label}
        title={label}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-xl leading-none text-tt-dark-purple shadow hover:bg-white"
      >
        <span aria-hidden="true" className={on ? 'text-red-600' : ''}>
          {on ? '♥' : '♡'}
        </span>
      </button>
      {problem && (
        <span
          role="alert"
          className="mt-1 max-w-[12rem] rounded bg-white/95 px-2 py-1 text-xs text-red-700 shadow"
        >
          {problem}
        </span>
      )}
    </span>
  );
};

export default FavouriteButton;
